import { ArrowRight } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";
import { glossaryCategories, visibleGlossaryTerms } from "../content/glossary";

export default function GlossaryPage() {
  const categories = glossaryCategories();

  // The hub is the `DefinedTermSet` the individual `DefinedTerm` pages point
  // back at, and `hasDefinedTerm` gives crawlers the full set from one fetch.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": "https://anter.ai/glossary",
    name: "Anter AI Agent Glossary",
    description:
      "Definitions of the terms used in production multi-agent AI systems — orchestration, routing, MCP tooling, permissions, and execution observability.",
    url: "https://anter.ai/glossary",
    hasDefinedTerm: visibleGlossaryTerms.map((term) => ({
      "@type": "DefinedTerm",
      "@id": `https://anter.ai/glossary/${term.slug}`,
      name: term.term,
      description: term.shortDefinition,
      url: `https://anter.ai/glossary/${term.slug}`,
    })),
  };

  return (
    <>
      <PageSEO
        title="AI Agent Glossary — Orchestration, MCP & Runtime Terms | Anter"
        description="Master the terminology of production AI. Explore our plain-language glossary of multi-agent orchestration, MCP servers, ReAct loops, and agent handoffs."
        canonical="/glossary"
        jsonLd={jsonLd}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
        <SiteHeader scrolled />

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-5xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Glossary" }]} />

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
            Reference
          </p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
            AI agent glossary
          </h1>
          <p className="mb-12 max-w-2xl text-lg leading-relaxed text-text-muted">
            The vocabulary of production multi-agent systems — what each term means, how it behaves
            in a real runtime, and the mistake people usually make with it.{" "}
            {visibleGlossaryTerms.length} terms across {categories.length} areas.
          </p>

          <div className="space-y-12">
            {categories.map(({ category, terms }) => (
              <section key={category}>
                <h2 className="mb-5 border-b border-border pb-2 text-sm font-bold uppercase tracking-[0.12em] text-text-muted">
                  {category}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {terms.map((term) => (
                    <a
                      key={term.slug}
                      href={`/glossary/${term.slug}`}
                      className="group rounded-2xl border border-border bg-bg-secondary/60 p-5 transition-all duration-200 hover:border-primary-border hover:bg-bg-hover"
                    >
                      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-text">
                        {term.term}
                        <ArrowRight className="h-3.5 w-3.5 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                      </h3>
                      <p className="text-sm leading-relaxed text-text-muted">
                        {term.shortDefinition}
                      </p>
                    </a>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <section className="mt-16 rounded-2xl border border-primary-border/60 bg-primary-dim/30 p-8">
            <h2 className="mb-2 text-xl font-extrabold tracking-tight text-text">
              These aren&apos;t abstractions — they&apos;re the runtime
            </h2>
            <p className="mb-5 max-w-2xl text-sm leading-relaxed text-text-muted">
              Every term here describes behaviour Anter actually enforces: validated routing graphs,
              independent transfer and delegation budgets, fail-closed permissions, and a full
              execution trace for every run.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/why-anter"
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                Why Anter
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/docs/runtime"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-5 py-2.5 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
              >
                Read the runtime docs
                <ArrowRight className="h-4 w-4 text-primary" />
              </a>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
